import { FC, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Dialogs as DialogAnalyticsId } from 'analytics/events/dialog';
import { observer } from 'mobx-react';

import { useStores } from 'mobx/hooks/useStores';

import PatientsFetcher, { SearchedPatient } from 'fetchers/PatientsFetcher';

import { DATE_ONLY_FORMAT, formatDate } from 'utils/DateUtils';

import Patient, { PatientSex } from 'models/Patient';

import { HEADER_HEIGHT } from 'containers/Layout/Layout.constants';

import { MergePatientsModalContent } from 'views/Modals/MergePatientsModal/MergePatientModalContent';
import {
  DuplicatePatientData,
  MergePatientsModalColumn
} from 'views/Modals/MergePatientsModal/MergePatientsModal.types';

import { AdvancedSearchBar } from 'components/UIkit/atoms/AdvancedSearchBar/AdvancedSearchBar';
import { SelectedSearchedPatientBox } from 'components/UIkit/atoms/AdvancedSearchBar/SelectedSearchedPatientBox';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';
import { Modal } from 'components/UIkit/atoms/Modal';
import { Text } from 'components/UIkit/atoms/Text';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  originalPatientData: DuplicatePatientData;
}

export const MergePatientsModal: FC<Props> = observer(
  ({ isOpen, onClose, originalPatientData }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isFailed, setIsFailed] = useState(false);
    const [patientsData, setPatientsData] = useState([originalPatientData]);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const { providersStore, locationsStore } = useStores();

    const handleSearchedPatientClicked = (
      patient: SearchedPatient,
      options?: { isRecentPatient?: boolean; isKeyboardSource?: boolean; isNewPatient?: boolean }
    ) => {
      setPatientsData((prevPatients) => {
        const searchPatientsData: DuplicatePatientData = {
          id: patient.patientId,
          mrn: patient.mrn,
          firstName: patient.firstName,
          lastName: patient.lastName,
          dateOfBirth: patient.dob || '',
          sex: patient.birthSex ? Patient.getSexText(patient.birthSex as PatientSex) : '',
          provider: patient.providerId
            ? providersStore.getProviderById(patient.providerId)?.name || ''
            : '',
          location: patient.locationId
            ? locationsStore.getLocationById(patient.locationId)?.name || ''
            : ''
        };

        if (prevPatients.length === 1) {
          return prevPatients.concat(searchPatientsData);
        }
        return [prevPatients[0], searchPatientsData];
      });
      return Promise.resolve();
    };
    const mergePatients = async () => {
      setIsLoading(true);
      try {
        await PatientsFetcher.mergePatientsInternal({
          keepPatientId: patientsData[0].id,
          mergeAndRemovePatientId: patientsData[1].id
        });
      } catch (error) {
        setIsFailed(true);
      } finally {
        onClose();
        setPatientsData([originalPatientData]);
        setIsConfirmationDialogOpen(false);
        setIsLoading(false);
      }
    };

    const showConfirmationDialog = () => {
      setIsConfirmationDialogOpen(true);
    };

    const columns: MergePatientsModalColumn[] = useMemo(
      () => [
        {
          accessor: 'id',
          name: '',
          Cell: (cellValue) => (
            <Text key={cellValue} width="200px" textAlign="start" py={12}>
              {cellValue === String(originalPatientData.id)
                ? 'Keep Patient:'
                : 'Merge Data & Remove Patient:'}
            </Text>
          ),
          width: '200px'
        },
        {
          name: 'MRN',
          accessor: 'mrn'
        },
        {
          accessor: 'firstName',
          name: 'First Name'
        },
        {
          name: 'Last Name',
          accessor: 'lastName'
        },
        {
          name: 'Date of Birth',
          accessor: 'dateOfBirth',
          formatCell: (cellValue) => (cellValue ? formatDate(cellValue, DATE_ONLY_FORMAT) : '')
        },
        {
          name: 'Provider',
          accessor: 'provider'
        },
        {
          name: 'Location',
          accessor: 'location'
        },
        {
          name: 'Sex',
          accessor: 'sex'
        }
      ],
      [originalPatientData.id]
    );

    return (
      <>
        <MessageDialog
          id={DialogAnalyticsId.MergeError}
          isOpen={isFailed}
          title="Patient Merge Failed"
          handleClose={() => setIsFailed(false)}
          primaryActionProps={{ text: 'OK', onClick: () => setIsFailed(false) }}
        >
          <Text variant="button-medium-outline">
            An error occurred while merging the patient records. Please contact support if the issue
            persists.
          </Text>
        </MessageDialog>
        <MessageDialog
          id={DialogAnalyticsId.ConfirmMerge}
          title="Permanently Remove Patient"
          isOpen={isConfirmationDialogOpen}
          handleClose={onClose}
          primaryActionProps={{
            text: 'Merge & Remove',
            onClick: mergePatients,
            disabled: isLoading || patientsData.length < 2
          }}
          secondaryActionProps={{
            text: 'Cancel',
            onClick: () => {
              setIsConfirmationDialogOpen(false);
            }
          }}
        >
          {patientsData.length === 2 && (
            <Text variant="button-medium-outline">{`“${patientsData[1].firstName} ${patientsData[1].lastName} (MRN: ${patientsData[1].mrn})” will be permanently removed.
             Their data will be merged with “${patientsData[0].firstName} ${patientsData[0].lastName} (MRN: ${patientsData[0].mrn})”.`}</Text>
          )}
        </MessageDialog>
        <Modal
          size="large"
          isOpen={isOpen && !isConfirmationDialogOpen}
          title="Merge Patients"
          subtitle={`Merging patient records is a permanent action — please review the details carefully before proceeding. 
If unsure, consider seeking support to avoid any data loss or inaccuracies. `}
          confirmActions={[
            {
              text: 'Merge',
              disabled: isLoading || patientsData.length < 2,
              onClick: showConfirmationDialog
            }
          ]}
          closeAction={{
            text: 'Cancel',
            onClick: () => {
              onClose();
              setPatientsData([originalPatientData]);
            },
            disabled: isLoading
          }}
        >
          <StyledSearchBarContainer>
            {patientsData.length < 2 ? (
              <AdvancedSearchBar
                onPatientClick={handleSearchedPatientClicked}
                hasSameWidthModifier
                searchBarBoxProps={{
                  variant: 'light',
                  hasPasteMrnOption: true
                }}
                searchBarMenusProps={{
                  offset: [0, 4],
                  variant: 'rounded',
                  patientsMenu: {
                    hasAddNewPatientOption: false,
                    maxHeight: `calc(100vh - 155px - ${HEADER_HEIGHT * 2}px)`
                  }
                }}
              />
            ) : (
              <SelectedSearchedPatientBox
                clearSearch={() => setPatientsData([originalPatientData])}
                variant="light"
                patient={patientsData[1]}
              />
            )}
          </StyledSearchBarContainer>
          <MergePatientsModalContent columns={columns} duplicatePatientData={patientsData} />
        </Modal>
      </>
    );
  }
);

const StyledSearchBarContainer = styled(Box)`
  margin-bottom: 28px;
`;
